import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@ainkauf/core";
import { SyncEngine, mergeRows, type SyncedRow, type SyncedTable } from "@ainkauf/core";
import { db } from "@/db/dexie";
import { repariereGrundzutaten, repariereWarengruppen, weckeOutbox } from "@/db/repository";
import { aufOutbox } from "@/lib/sync-events";
import { DexieStorage } from "./dexie-storage";
import { SupabaseRemote } from "./supabase-remote";
import { uploadPendingPhotos } from "./photos";

/** Regelmäßiger Abgleich, auch wenn nichts passiert. Fängt verpasste Ereignisse auf. */
const INTERVAL_MS = 30_000;

/**
 * Wie lange nach einer lokalen Änderung gewartet wird, bevor sie hochgeht.
 * Ein Abschluss schreibt ein Dutzend Zeilen hintereinander, die sollen in
 * einem Stapel gehen und nicht in zwölf Anfragen.
 */
const NACHLAUF_MS = 150;

/**
 * Tabellen, deren Änderungen sofort ankommen sollen. Seit Migration 0014
 * auch der Einkauf selbst (wer gerade im Laden steht) und die Planung, die
 * zu zweit geführt wird.
 */
const REALTIME_TABLES: SyncedTable[] = ["items", "lists", "trips", "recipes", "recipe_ingredients", "pantry_items", "meal_plan_entries"];

type Runde = "voll" | "hochladen";

export interface SyncRunner {
  /** Sofort eine volle Runde laufen lassen. */
  trigger: () => void;
  stop: () => void;
}

/**
 * Hängt die Sync-Engine an den Browser: Intervall, Online-Ereignis, Rückkehr
 * in den Vordergrund, jede lokale Änderung, dazu Realtime für die eigene
 * Familie.
 *
 * Eingehende Realtime-Ereignisse landen in Dexie, nie direkt im Zustand der
 * Oberfläche (Regel 1). Die Ansichten hängen an Dexie und aktualisieren sich
 * dadurch von selbst.
 */
export function startSync(client: SupabaseClient<Database>, familyId: string): SyncRunner {
  const engine = new SyncEngine(new DexieStorage(), new SupabaseRemote(client));
  let stopped = false;
  let inFlight: Promise<void> | null = null;
  let nachholen: Runde | null = null;
  let wecker: number | null = null;

  // Was in einer früheren Fassung als aussichtslos geparkt wurde, bekommt
  // beim Start eine neue Chance. Der Grund des Scheiterns kann mit genau
  // dieser Fassung behoben worden sein.
  void weckeOutbox().then((anzahl) => {
    if (anzahl > 0) console.info(`${String(anzahl)} wartende Änderungen wieder aufgenommen`);
  });

  /*
    Die Reparatur der Warengruppen läuft nach der ersten vollen Runde, nicht
    beim Start: Auf einem frischen Gerät ist Dexie beim Start leer, und eine
    Reparatur ohne Daten repariert nichts. Einmal je Sitzung genügt.
  */
  let warengruppenGeprueft = false;

  const runde = async (art: Runde) => {
    if (art === "voll") await engine.runOnce();
    else await engine.push();
    // Bilder danach: sie sind groß, und ein hängender Upload soll die Liste
    // nicht aufhalten.
    await uploadPendingPhotos(client);

    if (art === "voll" && !warengruppenGeprueft) {
      warengruppenGeprueft = true;
      const repariert = await repariereWarengruppen(familyId);
      if (repariert > 0) console.info(`${String(repariert)} Zeilen haben eine Warengruppe bekommen`);
      // Aus demselben Grund hier und nicht beim Start: Die Rezepte müssen da
      // sein, bevor jemand ihre Zutaten anschaut.
      const grundzutaten = await repariereGrundzutaten(familyId);
      if (grundzutaten > 0) console.info(`${String(grundzutaten)} Zutaten sind jetzt "immer im Haus"`);
    }
  };

  /*
    Läuft schon eine Runde, wird die nächste vorgemerkt statt verworfen.
    Sonst bliebe ein Haken, der während des Hochladens gesetzt wurde, bis zum
    nächsten Tick liegen, und der ist bis zu 30 Sekunden entfernt. Eine volle
    Runde schlägt eine reine Upload-Runde.
  */
  const run = (art: Runde = "voll") => {
    if (stopped || !navigator.onLine) return;
    if (inFlight) {
      nachholen = art === "voll" || nachholen === "voll" ? "voll" : "hochladen";
      return;
    }
    inFlight = runde(art)
      .catch((error: unknown) => {
        console.warn("Sync-Runde fehlgeschlagen:", error);
      })
      .finally(() => {
        inFlight = null;
        const naechste = nachholen;
        nachholen = null;
        if (naechste !== null) run(naechste);
      });
  };

  /*
    Nach einer lokalen Änderung nur hochladen, nicht alle Tabellen abfragen.
    Was die anderen geändert haben, bringt Realtime, und der volle Abgleich
    kommt ohnehin alle 30 Sekunden. Ein Haken kostet so eine Anfrage, nicht
    fünfzehn.
  */
  const sofort = () => {
    if (wecker !== null) window.clearTimeout(wecker);
    wecker = window.setTimeout(() => {
      wecker = null;
      run("hochladen");
    }, NACHLAUF_MS);
  };

  const onVisible = () => {
    if (document.visibilityState === "visible") run();
  };
  const onOnline = () => {
    run();
  };

  const interval = window.setInterval(() => {
    run();
  }, INTERVAL_MS);
  window.addEventListener("online", onOnline);
  document.addEventListener("visibilitychange", onVisible);
  const abOutbox = aufOutbox(sofort);

  const channel = client.channel(`familie:${familyId}`);
  for (const table of REALTIME_TABLES) {
    channel.on(
      "postgres_changes",
      { event: "*", schema: "public", table, filter: `family_id=eq.${familyId}` },
      (payload: { new: Record<string, unknown> | null }) => {
        void applyRealtimeRow(table, payload.new);
      },
    );
  }

  // Kommt die Verbindung nach einem Abriss zurück, holt eine volle Runde
  // nach, was in der Zwischenzeit passiert ist. Realtime liefert nur, was
  // während der Verbindung geschieht.
  let ersteVerbindung = true;
  void channel.subscribe((status) => {
    if (status !== "SUBSCRIBED") return;
    if (ersteVerbindung) {
      ersteVerbindung = false;
      return;
    }
    run();
  });

  run();

  return {
    trigger: () => {
      run();
    },
    stop: () => {
      stopped = true;
      window.clearInterval(interval);
      if (wecker !== null) window.clearTimeout(wecker);
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVisible);
      abOutbox();
      void client.removeChannel(channel);
    },
  };
}

/**
 * Ein einzelnes Ereignis in Dexie einspielen. Es gilt dieselbe Konfliktregel
 * wie beim Pull: ein eigener, jüngerer Stand gewinnt gegen das Ereignis.
 */
async function applyRealtimeRow(table: SyncedTable, row: Record<string, unknown> | null): Promise<void> {
  if (!row || typeof row.id !== "string") return;
  const incoming = row as unknown as SyncedRow;
  const existing = await db.table<SyncedRow>(table).get(incoming.id);
  const writes = mergeRows(existing ? [existing] : [], [incoming]);
  if (writes.length > 0) await db.table<SyncedRow>(table).bulkPut(writes);
}
